<div [class]="layout.shell">
  <header [class]="layout.header">
    <div class="flex items-center gap-2">
      <mat-icon class="h-5! w-5! shrink-0 text-xl! leading-5! text-primary-600">{{ isCreate ? "note_add" : "edit_note" }}</mat-icon>
      <h2
        id="guarantee-form-title"
        class="min-w-0 flex-1 truncate text-lg! leading-5! font-semibold! text-neutral-800"
      >
        {{ (isCreate ? "guaranteeV2.action.createGuarantee" : "guaranteeV2.action.editGuarantee") | translate }}
      </h2>
    </div>
    <p class="pl-7 text-sm text-neutral-500">{{ stepHintKey[step()] | translate }}</p>
  </header>

  <!-- A route, not a progress bar: any answered step can be returned to. -->
  <guarantees-step-nav
    class="shrink-0 px-4 pb-3"
    [steps]="stepEntries()"
    [current]="step()"
    (selected)="goTo($any($event))"
  ></guarantees-step-nav>

  <div [class]="layout.body">
    @switch (step()) {
      @case ("visit") {
        <section class="flex flex-col gap-2">
          <guarantees-appointment-picker
            [memberId]="data.memberId"
            [selectedId]="appointmentId()"
            (chosen)="chooseAppointment($event)"
          ></guarantees-appointment-picker>

          <!-- A second guarantee on one visit is legitimate but rarely meant, so offer the existing ones. -->
          @if (existingGuarantees().length > 0) {
            <div class="flex flex-col gap-1 rounded-lg border border-warning-200 bg-warning-50/50 px-2 py-1.5">
              <span class="flex items-center gap-1.5 text-sm font-semibold text-warning-800">
                <mat-icon class="h-4! w-4! shrink-0 text-base! leading-4!">verified</mat-icon>
                {{ "guaranteeV2.message.appointmentAlreadyGuaranteed" | translate: { count: existingGuarantees().length } }}
              </span>

              @for (visit of existingGuarantees(); track visit.guaranteeId) {
                <button
                  class="flex items-center gap-2 rounded-md bg-neutral-0 px-2 py-1 text-left text-sm transition-colors duration-150 hover:bg-primary-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
                  type="button"
                  [matTooltip]="'guaranteeV2.action.openExistingGuarantee' | translate"
                  (click)="openExisting(visit.guaranteeId)"
                >
                  <mat-icon class="h-4! w-4! shrink-0 text-base! leading-4! text-primary-500">north_east</mat-icon>
                  <span class="shrink-0 font-semibold text-neutral-700 tabular-nums">{{ visit.date | date: "d MMM yyyy" }}</span>
                  <span class="min-w-0 flex-1 truncate text-neutral-500">{{ visit.partnerName }}</span>
                  <span class="shrink-0 text-neutral-400 tabular-nums">{{ "guaranteeV2.label.linesCount" | translate: { count: visit.prescriptionCount } }}</span>
                </button>
              }
            </div>
          }

          <!-- A decision, not a default: it is stated rather than reached by leaving the step empty. -->
          <button
            guaranteesToggle
            [pressed]="visitSkipped()"
            shape="card"
            layout="block"
            size="lg"
            type="button"
            (click)="skipVisit()"
          >
            <mat-icon
              class="h-4! w-4! shrink-0 text-base! leading-4!"
              [class]="visitSkipped() ? 'text-primary-600' : 'text-neutral-300'"
              >{{ visitSkipped() ? "radio_button_checked" : "radio_button_unchecked" }}</mat-icon
            >
            <span class="flex min-w-0 flex-col">
              <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.action.withoutAppointment" | translate }}</span>
              <span class="text-sm text-neutral-400">{{ "guaranteeV2.message.withoutAppointmentHint" | translate }}</span>
            </span>
          </button>

          <!-- What the choice settles, said where it is made rather than asked again further on. -->
          @if (appointmentId() || visitSkipped()) {
            <guarantees-callout icon="info">
              {{ (isServiceRequest() ? "guaranteeV2.message.derivedServiceRequest" : "guaranteeV2.message.derivedDoctorVisit") | translate }}
            </guarantees-callout>
          } @else {
            <span class="text-sm text-error-600">{{ "guaranteeV2.message.stepVisitRequired" | translate }}</span>
          }
        </section>
      }

      @case ("details") {
        <section class="flex flex-col gap-3">
          <label class="flex flex-col gap-1">
            <span guaranteesLabel>{{ "guaranteeV2.label.guaranteeDescription" | translate }}</span>
            <textarea
              guaranteesControl
              class="w-full resize-y"
              name="description"
              rows="3"
              autocomplete="off"
              [placeholder]="'guaranteeV2.message.guaranteeDescriptionPlaceholder' | translate"
              [ngModel]="description()"
              (ngModelChange)="description.set($event)"
            ></textarea>
          </label>

          @if (isCreate) {
            <fieldset class="flex flex-col gap-1.5">
              <legend
                guaranteesLabel
                class="pb-1"
              >
                {{ "guaranteeV2.label.guaranteeDiagnoses" | translate }}
              </legend>

              @for (draft of diagnoses(); track draft.coding.code) {
                <div
                  class="flex flex-wrap items-center gap-2 rounded-lg border border-neutral-200 px-2 py-1"
                  data-testid="diagnosis-draft"
                >
                  <span class="shrink-0 rounded-md bg-neutral-100 px-1.5 py-0.5 font-mono text-sm font-semibold text-neutral-700">{{ draft.coding.code }}</span>
                  <span class="min-w-0 flex-1 truncate text-sm text-neutral-600">{{ draft.coding.name }}</span>

                  <!--
                  What the code is to this guarantee, which the care plan groups and reads by. Opens
                  on primary, and stays answered either way: a guarantee may carry as many primaries
                  as the visit had conditions.
                -->
                  <span [class]="segmentedGroup">
                    <button
                      guaranteesToggle
                      [pressed]="draft.role === roles.Primary"
                      size="xs"
                      shape="segment"
                      type="button"
                      [matTooltip]="'guaranteeV2.message.primaryDiagnosisHint' | translate"
                      (click)="setRole(draft.coding.code, roles.Primary)"
                    >
                      {{ "guaranteeV2.label.rolePrimary" | translate }}
                    </button>
                    <button
                      guaranteesToggle
                      [pressed]="draft.role === roles.Comorbidity"
                      size="xs"
                      shape="segment"
                      type="button"
                      [matTooltip]="'guaranteeV2.message.secondaryDiagnosisHint' | translate"
                      (click)="setRole(draft.coding.code, roles.Comorbidity)"
                    >
                      {{ "guaranteeV2.label.roleComorbidity" | translate }}
                    </button>
                  </span>

                  <button
                    class="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-neutral-400 hover:bg-error-50 hover:text-error-600 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
                    type="button"
                    [matTooltip]="'guaranteeV2.action.removeDiagnosis' | translate"
                    (click)="removeDiagnosis(draft.coding.code)"
                  >
                    <mat-icon class="h-4! w-4! text-base! leading-4!">close</mat-icon>
                  </button>
                </div>
              }

              @for (key of [pickerKey()]; track key) {
                <guarantees-icd10-picker
                  [taken]="takenDiagnosisCodes()"
                  (selected)="addDiagnosis($event)"
                ></guarantees-icd10-picker>
              }

              <!--
              Not an aside: nothing on the next step can be guaranteed until this one is answered, so
              it is stated at the weight of the refusal it is about rather than as background.
            -->
              @if (diagnoses().length === 0) {
                <guarantees-callout
                  stack
                  border
                  tone="warning"
                  icon="warning"
                >
                  <span class="font-semibold">{{ "guaranteeV2.label.noDiagnosesWarning" | translate }}</span>
                  <span>{{ "guaranteeV2.message.createWithoutDiagnosesHint" | translate }}</span>
                </guarantees-callout>
              }
            </fieldset>
          } @else {
            <guarantees-callout icon="info">
              {{ "guaranteeV2.message.editDiagnosesElsewhere" | translate }}
            </guarantees-callout>
          }
        </section>
      }

      @case ("prescriptions") {
        <section class="flex flex-col gap-2">
          @if (isCreate) {
            <!-- Queued: header, diagnoses and lines are created in one call. -->
            @for (group of queuedGroups(); track group.key) {
              <!-- Its own block, so the header hugs the lines under it rather than floating between groups. -->
              <section class="flex flex-col gap-1.5">
                <!-- The diagnosis over the lines it answers, headed as the care plan heads them. -->
                <header class="flex flex-wrap items-center gap-2">
                  @if (group.diagnosis; as diagnosis) {
                    <guarantees-diagnosis-chip
                      [diagnosis]="diagnosis"
                      [showCaseState]="false"
                    ></guarantees-diagnosis-chip>
                  } @else {
                    <!-- Not a cosmetic gap: cover cannot be granted on a line with no diagnosis behind it. -->
                    <span
                      class="inline-flex min-w-0 items-center gap-1 rounded-full border border-warning-300 bg-warning-100 px-2 py-0.5 text-sm font-semibold text-warning-800"
                      [matTooltip]="'guaranteeV2.message.withoutDiagnosisHint' | translate"
                    >
                      <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">warning</mat-icon>
                      {{ "guaranteeV2.label.withoutDiagnosis" | translate }}
                    </span>
                  }
                </header>

                @for (entry of group.entries; track entry.index) {
                  <!--
                The line's own position, named rather than read off `$index`: this row sits inside
                two loops already and carries a third, so `$index` here would name whichever of them
                is nearest rather than the line it means.
              -->
                  @let lineIndex = entry.index;
                  @let queued = entry.queued;
                  @let line = queued.prescription;
                  @let grantBlocker = grantBlockerKey(line);

                  <div class="flex flex-col gap-1.5 rounded-lg border border-neutral-200 px-2 py-1.5">
                    <div class="flex flex-wrap items-center gap-2">
                      <mat-icon
                        class="h-4! w-4! shrink-0 text-base! leading-4! text-neutral-400"
                        [matTooltip]="kindLabelKey[line.kind] | translate"
                        >{{ kindIcon[line.kind] }}</mat-icon
                      >
                      <span class="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-700">{{ line.name || line.code }}</span>
                      <!-- A course, as the care plan will show it: one order, this many lines. -->
                      @if (line.details?.quantity > 1) {
                        <guarantees-chip
                          class="shrink-0"
                          variant="outline"
                          [tooltip]="'guaranteeV2.message.setSizeHint' | translate: { count: line.details.quantity }"
                          >&times;{{ line.details.quantity }}</guarantees-chip
                        >
                      }
                      @if (line.code) {
                        <span
                          class="max-w-1/2 min-w-0 truncate rounded-md bg-neutral-100 px-1.5 py-0.5 font-mono text-sm text-neutral-600"
                          [matTooltip]="line.code"
                          >{{ line.code }}</span
                        >
                      }

                      <button
                        class="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
                        type="button"
                        [attr.aria-label]="'guaranteeV2.action.editLine' | translate"
                        [matTooltip]="'guaranteeV2.action.editLine' | translate"
                        (click)="addPrescription(lineIndex)"
                      >
                        <mat-icon class="h-4! w-4! text-base! leading-4!">edit</mat-icon>
                      </button>
                      <button
                        class="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-neutral-400 hover:bg-error-50 hover:text-error-600 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
                        type="button"
                        [attr.aria-label]="'guaranteeV2.action.deleteLine' | translate"
                        [matTooltip]="'guaranteeV2.action.deleteLine' | translate"
                        (click)="removePrescription(lineIndex)"
                      >
                        <mat-icon class="h-4! w-4! text-base! leading-4!">close</mat-icon>
                      </button>
                    </div>

                    <!--
                The ruling, given in the same pass as the line it answers. Left unanswered the line
                is raised undecided, which is where every line starts — so «без рішення» is one of
                the three and not the absence of a choice.
              -->
                    <div class="flex flex-col gap-1.5">
                      <span class="flex flex-wrap items-center gap-1.5">
                        <span [class]="segmentedGroup">
                          @for (answer of answers; track answer) {
                            <button
                              guaranteesToggle
                              size="xs"
                              shape="segment"
                              type="button"
                              [pressed]="queued.decision?.answer === answer"
                              [disabled]="answer === guaranteed && !!grantBlocker"
                              (click)="setDecision(lineIndex, answer)"
                            >
                              <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">{{ decisionIcon[answer] }}</mat-icon>
                              {{ decisionLabelKey[answer] | translate }}
                            </button>
                          }
                          <button
                            guaranteesToggle
                            size="xs"
                            shape="segment"
                            type="button"
                            [pressed]="!queued.decision"
                            (click)="setDecision(lineIndex)"
                          >
                            {{ "guaranteeV2.label.decisionPending" | translate }}
                          </button>
                        </span>

                        <!-- Beside the control rather than on it: a disabled button cannot carry a tooltip. -->
                        @if (grantBlocker) {
                          <span class="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning-100 px-2 py-0.5 text-xs font-semibold text-warning-800">
                            <mat-icon class="h-3! w-3! shrink-0 text-xs! leading-3!">warning</mat-icon>
                            {{ grantBlocker | translate }}
                          </span>
                        }
                      </span>

                      <!--
                  A row of its own, never beside the answer: the grounds are whole sentences, and
                  sharing a line with the control leaves them in a column too narrow to read.
                -->
                      @if (queued.decision; as decision) {
                        <guarantees-option-picker
                          wrap
                          maxHeight="max-h-56"
                          [options]="reasonOptions(decision.answer)"
                          [placeholder]="reasonPlaceholderKey(decision.answer) | translate"
                          [emptyMessage]="'guaranteeV2.message.reasonNoMatch' | translate"
                          [value]="decision.reason"
                          (valueChange)="setReason(lineIndex, $event)"
                        ></guarantees-option-picker>

                        @if (!decision.reason.trim()) {
                          <span class="text-sm text-error-600">{{ "guaranteeV2.message.decisionReasonRequired" | translate }}</span>
                        }
                      }
                    </div>
                  </div>
                }
              </section>
            }

            <button
              class="flex items-center gap-1.5 self-start rounded-full border border-dashed border-neutral-300 px-3 py-1.5 text-sm font-semibold text-neutral-600 transition-colors duration-150 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
              type="button"
              (click)="addPrescription()"
            >
              <mat-icon class="h-4! w-4! text-base! leading-4!">add</mat-icon>
              {{ "guaranteeV2.action.addPrescription" | translate }}
            </button>

            <!-- The review closed as part of raising the record, once nothing on it is still owed an answer. -->
            @if (lines().length > 0) {
              <button
                guaranteesToggle
                shape="card"
                layout="block"
                size="lg"
                type="button"
                [pressed]="markProcessed()"
                [disabled]="!canMarkProcessed()"
                (click)="markProcessed.set(!markProcessed())"
              >
                <guarantees-check-box [checked]="markProcessed()"></guarantees-check-box>
                <span class="flex min-w-0 flex-col">
                  <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.action.markProcessed" | translate }}</span>
                  <span class="text-sm text-neutral-400">{{
                    (canMarkProcessed() ? "guaranteeV2.message.markProcessedOnCreateHint" : "guaranteeV2.message.markProcessedBlocked") | translate
                  }}</span>
                </span>
              </button>
            }

            @if (lines().length === 0) {
              <guarantees-callout icon="info">
                {{ "guaranteeV2.message.createWithoutPrescriptionsHint" | translate }}
              </guarantees-callout>
            }
          } @else {
            <guarantees-callout icon="info">
              {{ "guaranteeV2.message.editPrescriptionsElsewhere" | translate }}
            </guarantees-callout>
          }
        </section>
      }
    }
  </div>

  <footer [class]="layout.footer">
    <button
      class="inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium text-neutral-500 transition-colors duration-150 hover:bg-neutral-100 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
      type="button"
      (click)="cancel()"
    >
      {{ "guaranteeV2.action.cancel" | translate }}
    </button>

    <span class="ml-auto flex items-center gap-1.5">
      @if (!isFirst()) {
        <button
          class="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-3 py-1.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
          type="button"
          (click)="back()"
        >
          <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">arrow_back</mat-icon>
          {{ "guaranteeV2.action.stepBack" | translate }}
        </button>
      }

      @if (!isLast()) {
        <button
          class="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-3 py-1.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:opacity-40"
          type="button"
          [disabled]="!canAdvance()"
          [matTooltip]="canAdvance() ? '' : ('guaranteeV2.message.stepVisitRequired' | translate)"
          (click)="next()"
        >
          {{ "guaranteeV2.action.stepNext" | translate }}
          <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">arrow_forward</mat-icon>
        </button>
      }

      <!-- Saving is reachable from every step: the later ones are optional, and the header is not. -->
      <button
        class="inline-flex items-center gap-1 rounded-full bg-primary-600 px-3 py-1.5 text-sm font-semibold text-neutral-0 transition-colors duration-150 hover:bg-primary-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:opacity-40 motion-reduce:transition-none"
        type="button"
        [disabled]="saving() || !canSubmit()"
        [matTooltip]="submitBlockedKey() ? (submitBlockedKey() | translate) : ''"
        (click)="submit()"
      >
        <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">{{ isCreate ? "note_add" : "save" }}</mat-icon>
        {{ (isCreate ? "guaranteeV2.action.createGuarantee" : "guaranteeV2.action.saveGuarantee") | translate }}
      </button>
    </span>
  </footer>
</div>
